import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BookOpen,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Hash,
  AlertCircle,
  CheckCircle2,
  Bookmark,
  Star,
  TrendingUp,
  List,
  Filter,
} from "lucide-react";
import { toast } from "react-toastify";
import { server } from "../server";

const AddChapter = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [chapters, setChapters] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isLoadingChapters, setIsLoadingChapters] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      chapter: "",
      isPopular: false,
      subjectId: "",
    },
  });

  console.log(selectedSubject);

  // Fetch subjects on component mount
  const fetchSubjects = async () => {
    try {
      setIsLoadingSubjects(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
        },
      });
      setSubjects(data?.data || []);
    } catch (error) {
      toast.error("Failed to load subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  // Fetch chapters based on selected subject
  const fetchChapters = async (subjectId = "") => {
    try {
      setIsLoadingChapters(true);
      const params = {
        pageNo: 1,
        pageSize: 100,
        subjectId,
      };

      const { data } = await axios.get(`${server}/chapter/all`, { params });
      setChapters(data?.data || []);
    } catch (error) {
      toast.error("Failed to load chapters");
      console.error("Error fetching chapters:", error);
    } finally {
      setIsLoadingChapters(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
    fetchChapters();
  }, []);

  // Fetch chapters when subject filter changes
  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);
    } else {
      fetchChapters();
    }
  }, [selectedSubject]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      if (!selectedSubject) {
        toast.error("Please select a subject");
        return;
      }

      const payload = {
        subjectId: parseInt(selectedSubject),
        chapter: data.chapter,
        isPopular: data.isPopular,
      };

      const { data: resData } = await axios.post(
        `${server}/chapter/new`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(resData?.message || "Chapter added successfully!");
      resetForm();

      // Refresh chapters list
      fetchChapters(selectedSubject);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create chapter");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    reset();
    setSelectedSubject("");
  };

  // Handle subject selection for both form and filter
  const handleSubjectChange = (subjectId) => {
    setSelectedSubject(subjectId);
    setValue("subjectId", subjectId);
  };

  // Clear subject filter
  const clearFilter = () => {
    setSelectedSubject("");
    setValue("subjectId", "");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    tap: { scale: 0.95 },
    hover: { scale: 1.05 },
  };

  const getSelectedSubjectName = () => {
    const subject = subjects.find(
      (sub) => sub.id === parseInt(selectedSubject)
    );
    return subject ? subject.subject : "";
  };

  const getSubjectNameById = (subjectId) => {
    const subject = subjects.find((sub) => sub.id === subjectId);
    return subject ? subject.subject : "Unknown Subject";
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      {/* Main Header */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-green-600 to-blue-700 rounded-2xl">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Chapter Management</h1>
        </div>
        <p className="text-gray-400 text-lg">
          Add new chapters and manage existing ones
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Left Card - Add Chapter Form */}
          <motion.div
            className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <Plus className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Add New Chapter
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Create a new chapter for your subject
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Subject Selection */}
                <motion.div variants={itemVariants}>
                  <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Bookmark className="w-5 h-5 text-blue-400" />
                    Select Subject *
                  </label>

                  {isLoadingSubjects ? (
                    <div className="flex items-center justify-center py-4">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <RefreshCw className="w-5 h-5 text-gray-400" />
                      </motion.div>
                      <span className="ml-2 text-gray-400">
                        Loading subjects...
                      </span>
                    </div>
                  ) : (
                    <>
                      <select
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white bg-gray-700 ${
                          errors.subjectId
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
                        }`}
                        value={selectedSubject}
                        onChange={(e) => handleSubjectChange(e.target.value)}
                      >
                        <option value="">Choose a subject</option>
                        {subjects.map((subject) => (
                          <option key={subject.id} value={subject.id}>
                            {subject.icon} {subject.subject}
                          </option>
                        ))}
                      </select>
                      {errors.subjectId && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="mt-2 text-sm text-red-400 flex items-center gap-1"
                        >
                          <AlertCircle className="w-4 h-4" />
                          {errors.subjectId.message}
                        </motion.p>
                      )}

                      {selectedSubject && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30"
                        >
                          <p className="text-sm text-blue-300">
                            Selected:{" "}
                            <span className="font-semibold">
                              {getSelectedSubjectName()}
                            </span>
                          </p>
                        </motion.div>
                      )}
                    </>
                  )}
                </motion.div>

                {/* Chapter Name */}
                <motion.div variants={itemVariants}>
                  <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Hash className="w-5 h-5 text-green-400" />
                    Chapter Name *
                  </label>
                  <input
                    type="text"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white placeholder-gray-500 bg-gray-700 ${
                      errors.chapter
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-600 focus:border-green-500 focus:ring-green-500/20"
                    }`}
                    placeholder="Enter chapter name (e.g., Algebra Basics, Organic Chemistry, Mechanics...)"
                    {...register("chapter", {
                      required: "Chapter name is required",
                      minLength: {
                        value: 2,
                        message: "Chapter name must be at least 2 characters",
                      },
                    })}
                  />
                  {errors.chapter && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-2 text-sm text-red-400 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.chapter.message}
                    </motion.p>
                  )}
                </motion.div>

                {/* Popular Chapter Toggle */}
                <motion.div variants={itemVariants}>
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
                        <TrendingUp className="w-5 h-5 text-orange-400" />
                      </div>
                      <div>
                        <span className="block text-sm font-semibold text-white">
                          Mark as Popular Chapter
                        </span>
                        <span className="block text-sm text-gray-400 mt-1">
                          This chapter will be highlighted as important
                        </span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      className="hidden"
                      {...register("isPopular")}
                    />
                    <div
                      className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                        watch("isPopular") ? "bg-green-500" : "bg-gray-600"
                      }`}
                    >
                      <motion.div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg ${
                          watch("isPopular") ? "left-7" : "left-1"
                        }`}
                        layout
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 30,
                        }}
                      />
                    </div>
                  </label>

                  {watch("isPopular") && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mt-3 p-3 bg-orange-500/20 rounded-lg border border-orange-500/30"
                    >
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-orange-400 fill-current" />
                        <span className="text-sm text-orange-300 font-medium">
                          This chapter will be marked as popular
                        </span>
                      </div>
                    </motion.div>
                  )}
                </motion.div>

                {/* Form Actions */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-4 pt-4"
                  variants={itemVariants}
                >
                  <motion.button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !selectedSubject ||
                      !watch("chapter") ||
                      watch("chapter").length < 2
                    }
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-br from-green-600 to-blue-700 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    variants={buttonVariants}
                    whileHover={
                      !isSubmitting &&
                      selectedSubject &&
                      watch("chapter")?.length >= 2
                        ? { scale: 1.02 }
                        : {}
                    }
                    whileTap={
                      !isSubmitting &&
                      selectedSubject &&
                      watch("chapter")?.length >= 2
                        ? { scale: 0.98 }
                        : {}
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        >
                          <RefreshCw className="w-5 h-5" />
                        </motion.div>
                        Adding Chapter...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Add Chapter
                      </>
                    )}
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={resetForm}
                    className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-all duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <RefreshCw className="w-5 h-5" />
                    Reset Form
                  </motion.button>
                </motion.div>
              </form>

              {/* Preview Section */}
              {(selectedSubject || watch("chapter")) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-gray-700/50 rounded-xl border border-gray-600"
                >
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Chapter Preview
                  </h3>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 w-16">Subject:</span>
                      <span className="text-white font-medium">
                        {getSelectedSubjectName() || "Not selected"}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 w-16">Chapter:</span>
                      <span className="text-white font-medium">
                        {watch("chapter") || "Not entered"}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 w-16">Status:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          watch("isPopular")
                            ? "bg-orange-500/30 text-orange-300 border border-orange-500/50"
                            : "bg-gray-600 text-gray-300 border border-gray-500"
                        }`}
                      >
                        {watch("isPopular")
                          ? "Popular Chapter"
                          : "Standard Chapter"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Right Card - Chapters List */}
          <motion.div
            className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
                    <List className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      All Chapters
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Manage existing chapters
                    </p>
                  </div>
                </div>

                {/* Subject Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    className="px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 text-white bg-gray-700 text-sm"
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                  >
                    <option value="">All Subjects</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              {/* Chapters List */}
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {isLoadingChapters ? (
                  <div className="flex items-center justify-center py-12">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <RefreshCw className="w-6 h-6 text-gray-400" />
                    </motion.div>
                    <span className="ml-3 text-gray-400">
                      Loading chapters...
                    </span>
                  </div>
                ) : chapters.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-base">
                      {selectedSubject
                        ? "No chapters found for selected subject"
                        : "No chapters found"}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      {selectedSubject
                        ? "Add a new chapter to get started"
                        : "Select a subject or add a new chapter"}
                    </p>
                  </div>
                ) : (
                  chapters.map((chapter, index) => (
                    <motion.div
                      key={chapter.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-purple-500/30 transition-all duration-200 group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-white font-semibold text-base group-hover:text-purple-300 transition-colors duration-200">
                              {chapter.chapter}
                            </h3>
                            {chapter.isPopular && (
                              <span className="px-2 py-1 bg-orange-500/30 text-orange-300 text-xs rounded-full border border-orange-500/50 flex items-center gap-1">
                                <Star className="w-3 h-3 fill-current" />
                                Popular
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Bookmark className="w-3 h-3" />
                            <span>{getSubjectNameById(chapter.subjectId)}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Summary */}
              {chapters.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 pt-4 border-t border-gray-700"
                >
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>
                      Showing {chapters.length} chapter
                      {chapters.length !== 1 ? "s" : ""}
                      {selectedSubject && ` for ${getSelectedSubjectName()}`}
                    </span>
                    {selectedSubject && (
                      <button
                        onClick={clearFilter}
                        className="text-purple-400 hover:text-purple-300 transition-colors duration-200 flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Clear filter
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AddChapter;
